import { onBeforeUnmount, onMounted, watch } from "vue";
import { useStore } from "vuex";
import http from "@/api/http.js";

/** Signal de présence : l'utilisateur reste « en ligne » tant que l'application est ouverte. */
const HEARTBEAT_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll"];
/** Synchronise le verrouillage entre les onglets ouverts. */
const LOCK_STORAGE_KEY = "ibacy:verrouillage";

/**
 * Présence et verrouillage de la session de l'espace d'administration :
 * - envoie un signal de présence chaque minute (fermer le navigateur fait passer hors ligne en quelques minutes) ;
 * - verrouille l'application après l'inactivité configurée (Paramètres > Validations et sécurité) ;
 * - propage le verrouillage aux autres onglets.
 */
export function useSessionGuard() {
  const store = useStore();
  let heartbeat = null;
  let idleTimer = null;
  let lastActivity = Date.now();

  const lockMinutes = () => Number(store.state.auth.lockMinutes) || 0;

  const sendHeartbeat = () => {
    if (!store.state.auth.isAuthenticated) return;
    http.post("presence", {}, { skipAuthRedirect: true }).catch(() => null);
  };

  const scheduleIdleLock = () => {
    clearTimeout(idleTimer);
    const minutes = lockMinutes();
    if (!minutes || store.state.auth.locked) return;
    const remaining = minutes * 60 * 1000 - (Date.now() - lastActivity);
    idleTimer = setTimeout(() => {
      if (Date.now() - lastActivity >= minutes * 60 * 1000) store.dispatch("auth/lock");
      else scheduleIdleLock();
    }, Math.max(remaining, 1000));
  };

  /** Une activité ne relance le minuteur qu'au plus toutes les 5 secondes (évite un travail à chaque mouvement). */
  const onActivity = () => {
    const now = Date.now();
    if (now - lastActivity < 5000) return;
    lastActivity = now;
    scheduleIdleLock();
  };

  const onStorage = (event) => {
    if (event.key !== LOCK_STORAGE_KEY || !event.newValue) return;
    const locked = event.newValue.startsWith("1");
    if (locked) store.commit("auth/setLocked", true);
    else if (store.state.auth.locked) store.dispatch("auth/fetchUser").catch(() => null);
  };

  const broadcastLock = (locked) => {
    try {
      localStorage.setItem(LOCK_STORAGE_KEY, `${locked ? 1 : 0}:${Date.now()}`);
    } catch (e) {
      // Stockage indisponible (navigation privée) : chaque onglet se verrouillera à sa prochaine requête.
    }
  };

  /** Au retour sur l'onglet, vérifie l'inactivité réelle (minuteurs ralentis en arrière-plan). */
  const onVisibility = () => {
    if (document.visibilityState !== "visible") return;
    const minutes = lockMinutes();
    if (minutes && Date.now() - lastActivity >= minutes * 60 * 1000) store.dispatch("auth/lock");
    sendHeartbeat();
  };

  const stopWatch = watch(
    () => store.state.auth.locked,
    (locked, previous) => {
      if (locked !== previous) broadcastLock(locked);
      if (!locked) {
        lastActivity = Date.now();
        scheduleIdleLock();
      } else {
        clearTimeout(idleTimer);
      }
    }
  );
  const stopMinutesWatch = watch(() => store.state.auth.lockMinutes, scheduleIdleLock);

  onMounted(() => {
    sendHeartbeat();
    heartbeat = setInterval(sendHeartbeat, HEARTBEAT_MS);
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    scheduleIdleLock();
  });

  onBeforeUnmount(() => {
    clearInterval(heartbeat);
    clearTimeout(idleTimer);
    stopWatch();
    stopMinutesWatch();
    ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
    window.removeEventListener("storage", onStorage);
    document.removeEventListener("visibilitychange", onVisibility);
  });
}
